// The contents the signed-in Waper has already reported: « Signaler » is greyed out on them, for this Waper only
// (one report per Waper and content, refused by the API with a 409 otherwise). The pages are cached and anonymous,
// so the list is fetched once per session from /secure/warn/mine, in the browser, after the page is shown. Keys are
// "<type>:<slug>" in the vocabulary of the warn route (post, bv_review, wcPost; comment with its id).
type MyWarns = { token: string | null; keys: Record<string, true>; loading: boolean }

export const warnKey = (type: string, slug: string) => `${type}:${slug}`

export const useMyWarns = () => {
  const state = useState<MyWarns>('myWarns', () => ({
    token: null,
    keys: {},
    loading: false,
  }))
  const { token, authenticated, isTemp, getAuthHeader } = useTempAuth()
  const { wsUrl } = useRuntimeConfig().public

  const load = async () => {
    const current = token.value
    // another account (or none) since the list was fetched: its keys are not this Waper's
    if (state.value.token && state.value.token !== current) {
      state.value = { token: null, keys: {}, loading: false }
    }
    if (
      import.meta.server ||
      !authenticated.value ||
      isTemp.value ||
      !current ||
      state.value.token === current ||
      state.value.loading
    ) {
      return
    }
    state.value.loading = true
    // $fetch, not useFetch: called from onMounted while the page hydrates, useFetch queues its request for a
    // before-mount that already ran and never sends it (empty list, nothing greyed out; seen on preprod 03/11)
    const keys = await $fetch<string[]>(API.GET_MY_WARNS, {
      baseURL: wsUrl,
      headers: getAuthHeader({
        authenticated,
        isTemp,
        token,
        url: API.GET_MY_WARNS,
      }),
    }).catch(() => [] as string[])
    // kept even on an error: no retry storm, the 409 of a second report still says it
    state.value = {
      token: current,
      keys: {
        ...state.value.keys,
        ...Object.fromEntries(
          (Array.isArray(keys) ? keys : []).map((k) => [k, true]),
        ),
      },
      loading: false,
    }
  }

  // every component showing a « Signaler » asks for it; only the first call fetches. Again after a login on the page.
  onMounted(() => {
    load()
    watch(token, load)
  })

  const isReported = (type: string, slug: string) =>
    authenticated.value && !!state.value.keys[warnKey(type, slug)]

  const markReported = (type: string, slug: string) => {
    state.value.keys = { ...state.value.keys, [warnKey(type, slug)]: true }
  }

  return { isReported, markReported }
}
